// Presentation only. Stored intent never supplies identity, permissions or capability.
export const FEEDBACK_KEY = "places.devActAsFeedback";
export const SELF_KEY = "places.devActAsSelf";
export const MAX_PENDING_MS = 45000;
export const CONFIRM_MS = 1500;
const SELF_MAX_MS = 12 * 60 * 60 * 1000;
const listeners = new Set();
let capability = false;
let verifiedSelf = null;

function remove(key) {
  try { sessionStorage.removeItem(key); } catch { /* Storage can be unavailable. */ }
}
function read(key) {
  try { return JSON.parse(sessionStorage.getItem(key)); } catch { remove(key); return null; }
}
function write(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* In-memory feedback still works. */ }
}
const text = value => typeof value === "string" && value.length > 0 && value.length <= 300;
const fresh = (at, max) => Number.isFinite(at) && at <= Date.now() && Date.now() - at < max;
function restore() {
  const saved = read(FEEDBACK_KEY);
  if (saved && saved.schema === 1 && fresh(saved.at, MAX_PENDING_MS) &&
      ["request", "reload"].includes(saved.phase) &&
      (saved.targetId === null || text(saved.targetId)) &&
      (saved.targetId === null ? saved.name === null || text(saved.name) : text(saved.name)) &&
      (saved.selfId === null || text(saved.selfId)) &&
      (saved.phase === "request" ? saved.version === null : text(saved.version))) {
    const { schema, at, phase, targetId, name, selfId, version } = saved;
    return { schema, at, phase, targetId, name, selfId, version };
  }
  remove(FEEDBACK_KEY);
  return null;
}
let feedback = restore();
let snapshot = { enabled: false, feedback: null };
const publish = () => {
  snapshot = { enabled: capability, feedback: capability ? feedback : null };
  listeners.forEach(listener => listener());
};
export const subscribeFeedback = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export const getFeedbackSnapshot = () => snapshot;

// Retained context is presentation only, scoped to the controller ID still
// carried by /api/me while acting. Never infer self from the target's role/name.
export function getSelfPresentation(user, status) {
  if (!capability || status?.enabled !== true) return null;
  if (!status.person && !status.invalid && !user?.devActAs) {
    return text(user?.display_name) ? { name: user.display_name, role: user.role } : null;
  }
  const self = verifiedSelf ?? read(SELF_KEY);
  return self && self.id === user?.id && text(self.name) && fresh(self.at, SELF_MAX_MS)
    ? { name: self.name, role: self.role } : null;
}

export function setFeedbackCapability(status) {
  capability = status?.enabled === true;
  if (!capability) {
    feedback = null;
    verifiedSelf = null;
    remove(FEEDBACK_KEY);
    remove(SELF_KEY);
  }
  publish();
}

export function failFeedback(error) {
  if (!feedback || feedback.phase === "success") return;
  remove(FEEDBACK_KEY);
  feedback = { ...feedback, phase: "error", message: error.message };
  publish();
}

export function dismissFeedback() {
  feedback = null;
  remove(FEEDBACK_KEY);
  publish();
}

export function beginFeedback(status, targetId, name = null) {
  if (status?.enabled !== true || (feedback && ["request", "reload"].includes(feedback.phase))) return false;
  capability = true; // A live backend response, never restored storage.
  const savedSelf = verifiedSelf ?? read(SELF_KEY);
  const self = savedSelf && text(savedSelf.id) && text(savedSelf.name) && fresh(savedSelf.at, SELF_MAX_MS)
    ? savedSelf : null;
  if (!self) remove(SELF_KEY);
  feedback = { schema: 1, at: Date.now(), phase: "request", targetId,
    name: targetId ? name : self?.name ?? null, selfId: self?.id ?? null, version: null };
  write(FEEDBACK_KEY, feedback);
  publish();
  return true;
}

export function commitFeedback(changed) {
  if (!feedback || feedback.phase !== "request") return;
  feedback = { ...feedback, phase: "reload", version: changed?.version ?? null };
  write(FEEDBACK_KEY, feedback);
  publish();
}

export function verifyFeedbackIdentity(user, status) {
  if (!capability) return;
  if (feedback && ["request", "reload"].includes(feedback.phase)) {
    const pending = feedback;
    const matchingVersion = text(pending.version) && status.version === pending.version;
    const matches = pending.targetId
      ? status.person?.id === pending.targetId && user?.devActAs?.person?.id === pending.targetId
        && user.devActAs.version === status.version
      : !status.person && !user?.devActAs && text(user?.id) && (!pending.selfId || user.id === pending.selfId);
    if (pending.phase !== "reload" || !matchingVersion || !matches || status.invalid) {
      failFeedback(new Error(status.invalid || "The requested profile could not be verified. Reload to check the current identity."));
    } else {
      const name = pending.targetId
        ? user.devActAs.person.displayName || user.devActAs.person.email
        : user.display_name;
      remove(FEEDBACK_KEY);
      feedback = { ...pending, phase: "success", message: name ? `Now viewing as ${name}` : "Now viewing as yourself" };
      publish();
    }
  }
  if (!status.person && !status.invalid && !user?.devActAs && text(user?.id) && text(user?.display_name)) {
    verifiedSelf = { id: user.id, name: user.display_name, role: user.role, at: Date.now() };
    write(SELF_KEY, verifiedSelf);
  }
}

export function feedbackProgress(pending) {
  return pending.targetId ? `Loading profile as ${pending.name}…`
    : pending.name ? `Returning to ${pending.name}…` : "Returning to your profile…";
}
